import { useEffect, useState } from "react";
import { Modal, Input, InputNumber, Select } from "antd";
import { EditOutlined, PlusCircleOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { posModalStyles as modalStyles } from "../../modalPrimitives";
import { useViewport } from "../../../../../hooks/useViewport";
import { useMenuVats } from "../../../../../hooks/useMenu";
import { R } from "../../orderingTheme";
import {
  GhostButton,
  ModalFooter,
  ModalHeader,
  PrimaryButton,
} from "../../modalPrimitives";
import type { Line } from "./InvoiceCreateModal";

interface Props {
  open: boolean;
  mode: "edit" | "create";
  initial: Line | null;
  onClose: () => void;
  // edit: подава целия line с promяните; create: подава нов line без id/total.
  onSubmit: (data: Omit<Line, "id" | "total">) => void;
}

const DEFAULT_UNITS = ["бр.", "кг.", "л.", "м.", "м²", "уп."];

export const InvoiceLineEditModal = ({
  open,
  mode,
  initial,
  onClose,
  onSubmit,
}: Props) => {
  const { t } = useTranslation();
  const { isMobile, isNarrow, isTablet, isDesktop } = useViewport();
  const vats = useMenuVats();

  const [name, setName] = useState("");
  const [unit, setUnit] = useState("бр.");
  const [vatRate, setVatRate] = useState<number>(0);
  const [quantity, setQuantity] = useState<number | null>(1);
  const [unitPrice, setUnitPrice] = useState<number | null>(0);

  useEffect(() => {
    if (!open) return;
    if (mode === "edit" && initial) {
      setName(initial.name);
      setUnit(initial.unit);
      setVatRate(initial.vatRate);
      setQuantity(initial.quantity);
      setUnitPrice(initial.unitPrice);
    } else {
      setName("");
      setUnit("бр.");
      setVatRate(vats[0]?.vatRate ?? 0);
      setQuantity(null);
      setUnitPrice(null);
    }
  }, [open, mode, initial, vats]);

  const canSave = name.trim().length > 0;

  // Esc/Enter shortcuts в стила на другите invoicing модали.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key === "Enter" && !(e.target instanceof HTMLInputElement)) {
        e.preventDefault();
        if (canSave) {
          onSubmit({
            name: name.trim(),
            unit: unit || "бр.",
            vatRate: Number(vatRate ?? 0),
            quantity: Number(quantity ?? 0),
            unitPrice: Number(unitPrice ?? 0),
          });
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, canSave, name, unit, vatRate, quantity, unitPrice, onClose, onSubmit]);

  const handleSave = () => {
    if (!canSave) return;
    onSubmit({
      name: name.trim(),
      unit: unit || "бр.",
      vatRate: Number(vatRate ?? 0),
      quantity: Number(quantity ?? 0),
      unitPrice: Number(unitPrice ?? 0),
    });
  };

  const modalWidth = isMobile ? undefined : isTablet ? 520 : 620;
  const isEdit = mode === "edit";

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      closable={false}
      destroyOnHidden
      width={modalWidth}
      styles={modalStyles}
      style={{ maxWidth: "calc(100vw - 32px)" }}
    >
      <div
        className="inv-line"
        style={{
          background: R.surface,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <style>{`
          .inv-line .ant-input,
          .inv-line .ant-input-number,
          .inv-line .ant-select .ant-select-selector {
            border-radius: 10px !important;
            border-color: ${R.border} !important;
            background: ${R.surface} !important;
            min-height: 38px !important;
            font-size: 13px;
            color: ${R.ink};
          }
          .inv-line .ant-input { height: 38px !important; padding: 4px 12px !important; }
          .inv-line .ant-input-number { width: 100% !important; }
          /* AntD-та подравнява числовите input-и вдясно — в този модал
             искаме всички полета да изглеждат еднакво (left-align), за да
             не „щърбят" редицата с другите. */
          .inv-line .ant-input-number .ant-input-number-input {
            text-align: left !important;
            height: 38px !important;
            padding: 4px 12px !important;
          }
          .inv-line .ant-input:hover,
          .inv-line .ant-input:focus,
          .inv-line .ant-input-number:hover,
          .inv-line .ant-input-number-focused,
          .inv-line .ant-select:hover .ant-select-selector,
          .inv-line .ant-select-focused .ant-select-selector {
            border-color: ${R.brand} !important;
            box-shadow: 0 0 0 3px rgba(217,119,6,0.12) !important;
          }
        `}</style>

        <ModalHeader
          icon={isEdit ? <EditOutlined /> : <PlusCircleOutlined />}
          title={
            isEdit
              ? t("invoiceCreate.editLineTitle")
              : t("invoiceCreate.newLineTitle")
          }
          subtitle={
            isEdit
              ? t("invoiceCreate.editLineSubtitle")
              : t("invoiceCreate.newLineSubtitle")
          }
          onClose={onClose}
          isNarrow={isNarrow}
        />

        <div
          style={{
            padding: isNarrow ? "14px 16px" : "18px 24px",
            background: R.bg,
            display: "flex",
            flexDirection: "column",
            gap: 12,
          }}
        >
          <FormField label={t("invoiceCreate.colName")} required>
            <Input
              value={name}
              maxLength={255}
              onChange={(e) => setName(e.target.value)}
              autoFocus
            />
          </FormField>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: isNarrow ? "1fr 1fr" : "1fr 1fr 1fr 1fr",
              gap: 10,
            }}
          >
            <FormField label={t("invoiceCreate.colUnit")}>
              <Select
                value={unit}
                onChange={setUnit}
                style={{ width: "100%" }}
                options={DEFAULT_UNITS.map((u) => ({ value: u, label: u }))}
                showSearch
              />
            </FormField>
            <FormField label={t("invoiceCreate.colVat")}>
              <Select
                value={vatRate}
                onChange={(v) => setVatRate(Number(v))}
                style={{ width: "100%" }}
                options={vats.map((v) => ({
                  value: v.vatRate,
                  label: `${v.vatRate}%`,
                }))}
              />
            </FormField>
            <FormField label={t("invoiceCreate.colQty")}>
              <InputNumber
                value={quantity}
                onChange={(v) =>
                  setQuantity(v === null || v === undefined ? null : Number(v))
                }
                min={0}
                step={1}
                style={{ width: "100%" }}
              />
            </FormField>
            <FormField label={t("invoiceCreate.colUnitPrice")}>
              <InputNumber
                value={unitPrice}
                onChange={(v) =>
                  setUnitPrice(v === null || v === undefined ? null : Number(v))
                }
                min={0}
                step={0.01}
                precision={2}
                style={{ width: "100%" }}
              />
            </FormField>
          </div>
        </div>

        <ModalFooter isNarrow={isNarrow} narrowFirst>
          <GhostButton
            label={t("common.cancel")}
            onClick={onClose}
            shortcut={isDesktop ? "Esc" : undefined}
          />
          <PrimaryButton
            label={
              isEdit
                ? t("invoiceCreate.save")
                : t("invoiceCreate.addLine")
            }
            onClick={handleSave}
            disabled={!canSave}
            shortcut={isDesktop ? "⏎" : undefined}
          />
        </ModalFooter>
      </div>
    </Modal>
  );
};

function FormField({
  label,
  children,
  required,
}: {
  label: string;
  children: React.ReactNode;
  required?: boolean;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
      <span
        style={{
          fontSize: 11,
          fontWeight: 600,
          color: R.textSoft,
          letterSpacing: 0.2,
        }}
      >
        {label}
        {required && <span style={{ color: R.danger, marginLeft: 3 }}>*</span>}
      </span>
      {children}
    </div>
  );
}

export default InvoiceLineEditModal;
